import { useCallback, useEffect, useState } from 'react';
import {
  opsApi,
  type OpsCueDto, type OpsCueInput, type OpsCueStatus,
  type OpsDepartmentDto, type OpsDockDto, type OpsDockInput, type OpsDockStatus,
  type OpsDockSummaryDto, type OpsStaffSummaryDto,
} from '../api';
import { OpsPanelState, OpsTable, RowActions, opsErrorText, useOpsPanel } from './ops/panel';

/**
 * TICK-124 — showday: the running order, and the load-in board.
 *
 * Two lists on one screen because on the day they are read the same way — by time, by whoever is
 * standing at the gate or on the deck — and splitting them across two navigation items would ask an
 * operator holding a radio to remember which one holds the truck.
 *
 * The dock board shows the truck plate and masks the driver's phone. Both fields are marked
 * sensitive and they are not the same kind of sensitive: a plate is read off a windscreen to match
 * a truck to a slot, and a phone number belongs to a person and is needed only when somebody has to
 * call them. The number is one deliberate click away, which is also what puts the request in the
 * audit trail.
 *
 * `fixedDeadline` is shown but never computed against. A cue that cannot move is a fact the operator
 * states; the ripple a schedule change would cause belongs to a cue engine that does not exist yet,
 * and inventing half of one here would produce times nobody could trace.
 *
 * TICK-136 S3 — the owner column reads `ownerName` first and falls back to `importedOwnerName`,
 * the same two-value shape `OpsTasksPanel` has had since TICK-122. Before 0068 these two tables had
 * only the reference, so an import that could not match a name had nowhere to put it; now an
 * unmatched row says whose name came in and that nobody is attached to it yet.
 */

const CUE_STATUS_LABEL: Record<OpsCueStatus, string> = {
  pending: 'ยังไม่ถึงคิว',
  ready: 'พร้อม',
  executed: 'ทำแล้ว',
  delayed: 'ล่าช้า',
};

const DOCK_STATUS_LABEL: Record<OpsDockStatus, string> = {
  scheduled: 'นัดไว้',
  arrived: 'ถึงแล้ว',
  unloading: 'กำลังลงของ',
  completed: 'เสร็จแล้ว',
};

const CUE_STATUS_ORDER: OpsCueStatus[] = ['pending', 'ready', 'executed', 'delayed'];
const DOCK_STATUS_ORDER: OpsDockStatus[] = ['scheduled', 'arrived', 'unloading', 'completed'];

export function OpsShowdayPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [cues, setCues] = useState<OpsCueDto[] | null>(null);
  const [docks, setDocks] = useState<OpsDockSummaryDto[]>([]);
  const [departments, setDepartments] = useState<OpsDepartmentDto[]>([]);
  const [crew, setCrew] = useState<OpsStaffSummaryDto[]>([]);
  const [loadError, setLoadError] = useState('');
  const [cueForm, setCueForm] = useState<{ mode: 'create' } | { mode: 'edit'; cue: OpsCueDto } | null>(null);
  const [dockForm, setDockForm] = useState<{ mode: 'create' } | { mode: 'edit'; dock: OpsDockDto } | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, k, d, s] = await Promise.all([
        opsApi.listCues(token, eventSlug),
        opsApi.listDocks(token, eventSlug),
        opsApi.listDepartments(token, eventSlug),
        opsApi.listStaff(token, eventSlug),
      ]);
      setCues(c);
      setDocks(k);
      setDepartments(d);
      setCrew(s);
    } catch (e) {
      setLoadError(opsErrorText(e, 'โหลดข้อมูลวันแสดงไม่สำเร็จ'));
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  const { busy, error, setError, run } = useOpsPanel(load);

  if (!cues) return <OpsPanelState error={loadError} />;

  return (
    <div className="ops-panel">
      {error && <div className="form-error">{error}</div>}

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>คิวการแสดง</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
              ลำดับงานบนเวทีเรียงตามเวลา · “เวลาตายตัว” หมายถึงคิวที่เลื่อนไม่ได้ ระบบบันทึกไว้แต่ยังไม่คำนวณผลกระทบให้
            </p>
          </div>
          <button className="btn-primary" disabled={busy} onClick={() => setCueForm({ mode: 'create' })}>เพิ่มคิว</button>
        </div>

        {cues.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีคิวการแสดงของอีเวนต์นี้</div>
        ) : (
          <OpsTable head={<><th>เวลา</th><th>คิว</th><th className="num">นาที</th><th>ฝ่าย</th><th>ผู้รับผิดชอบ</th><th>สถานะ</th><th /></>}>
                {cues.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
                      {c.time}{c.fixedDeadline && <span title="เวลาตายตัว เลื่อนไม่ได้" style={{ marginLeft: 6, color: 'var(--sv-warn)' }}>ตายตัว</span>}
                    </td>
                    <td>{c.title}</td>
                    <td className="num" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{c.durationMinutes}</td>
                    <td style={{ color: 'var(--dim)' }}>{c.departmentName ?? '—'}</td>
                    <td>{c.ownerName ?? (c.importedOwnerName
                      ? <span style={{ color: 'var(--dim)' }} title="ชื่อจากการนำเข้า ยังไม่ได้ผูกกับคนในทีมงาน">{c.importedOwnerName} · ยังไม่ผูก</span>
                      : <span style={{ color: 'var(--dim)' }}>—</span>)}</td>
                    <td>
                      <select
                        aria-label={`สถานะคิว ${c.title}`}
                        value={c.status}
                        disabled={busy}
                        onChange={(e) => void run(() => opsApi.updateCue(
                          token, c.id, { status: e.target.value as OpsCueStatus, version: c.version }, eventSlug,
                        ))}
                      >
                        {CUE_STATUS_ORDER.map((s) => <option key={s} value={s}>{CUE_STATUS_LABEL[s]}</option>)}
                      </select>
                    </td>
                    <td>
                      <RowActions>
                        <button className="btn-link" disabled={busy} onClick={() => setCueForm({ mode: 'edit', cue: c })}>แก้ไข</button>
                        <button
                          className="btn-link"
                          disabled={busy}
                          onClick={() => {
                            if (!confirm(`ลบคิว "${c.title}"?`)) return;
                            void run(() => opsApi.deleteCue(token, c.id, c.version, eventSlug));
                          }}
                        >
                          ลบ
                        </button>
                      </RowActions>
                    </td>
                  </tr>
                ))}
          </OpsTable>
        )}

        {cueForm && (
          <CueEditor
            cue={cueForm.mode === 'edit' ? cueForm.cue : null}
            departments={departments}
            crew={crew}
            busy={busy}
            onCancel={() => setCueForm(null)}
            onSave={async (input) => {
              const ok = await run(() => (cueForm.mode === 'edit'
                ? opsApi.updateCue(token, cueForm.cue.id, { ...input, version: cueForm.cue.version }, eventSlug)
                : opsApi.createCue(token, input, eventSlug)));
              if (ok) setCueForm(null);
            }}
          />
        )}
      </section>

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>ช่องลงของ (Load-in)</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
              ตารางนี้แสดงทะเบียนรถเพื่อให้จับคู่รถกับช่องได้ แต่ปิดบังเบอร์คนขับ — กดดูรายคันจึงเห็นเบอร์เต็ม
            </p>
          </div>
          <button className="btn-primary" disabled={busy} onClick={() => setDockForm({ mode: 'create' })}>เพิ่มรอบลงของ</button>
        </div>

        {docks.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีรอบลงของ</div>
        ) : (
          <OpsTable head={<><th>ช่อง</th><th>เวลานัด</th><th>ผู้ขาย / อุปกรณ์</th><th>ทะเบียนรถ</th><th>เบอร์คนขับ</th><th>สถานะ</th><th /></>}>
                {docks.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{d.dockNumber}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{d.scheduledTime}{d.arrivedTime ? ` · ถึง ${d.arrivedTime}` : ''}</td>
                    <td>{d.vendorName}<span style={{ color: 'var(--dim)' }}> · {d.equipmentType}</span></td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{d.truckPlate}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--dim)' }}>{d.driverPhoneMasked}</td>
                    <td>
                      <select
                        aria-label={`สถานะช่อง ${d.dockNumber}`}
                        value={d.status}
                        disabled={busy}
                        onChange={(e) => void run(() => opsApi.updateDock(
                          token, d.id, { status: e.target.value as OpsDockStatus, version: d.version }, eventSlug,
                        ))}
                      >
                        {DOCK_STATUS_ORDER.map((s) => <option key={s} value={s}>{DOCK_STATUS_LABEL[s]}</option>)}
                      </select>
                    </td>
                    <td>
                      <RowActions>
                        <button
                          className="btn-link"
                          disabled={busy}
                          onClick={async () => {
                            setError('');
                            try {
                              setDockForm({ mode: 'edit', dock: await opsApi.getDock(token, d.id, eventSlug) });
                            } catch (e) {
                              setError(opsErrorText(e, 'เปิดข้อมูลรอบนี้ไม่สำเร็จ'));
                            }
                          }}
                        >
                          แก้ไข
                        </button>
                        <button
                          className="btn-link"
                          disabled={busy}
                          onClick={() => {
                            if (!confirm(`ลบรอบลงของช่อง "${d.dockNumber}"?`)) return;
                            void run(() => opsApi.deleteDock(token, d.id, d.version, eventSlug));
                          }}
                        >
                          ลบ
                        </button>
                      </RowActions>
                    </td>
                  </tr>
                ))}
          </OpsTable>
        )}

        {dockForm && (
          <DockEditor
            dock={dockForm.mode === 'edit' ? dockForm.dock : null}
            busy={busy}
            onCancel={() => setDockForm(null)}
            onSave={async (input) => {
              const ok = await run(() => (dockForm.mode === 'edit'
                ? opsApi.updateDock(token, dockForm.dock.id, { ...input, version: dockForm.dock.version }, eventSlug)
                : opsApi.createDock(token, input, eventSlug)));
              if (ok) setDockForm(null);
            }}
          />
        )}
      </section>
    </div>
  );
}

function CueEditor({ cue, departments, crew, busy, onCancel, onSave }: {
  cue: OpsCueDto | null;
  departments: OpsDepartmentDto[];
  crew: OpsStaffSummaryDto[];
  busy: boolean;
  onCancel: () => void;
  onSave: (input: OpsCueInput) => void;
}) {
  const [title, setTitle] = useState(cue?.title ?? '');
  const [time, setTime] = useState(cue?.time ?? '');
  const [durationMinutes, setDurationMinutes] = useState(String(cue?.durationMinutes ?? 15));
  const [departmentId, setDepartmentId] = useState(cue?.departmentId ?? '');
  const [ownerStaffId, setOwnerStaffId] = useState(cue?.ownerStaffId ?? '');
  const [fixedDeadline, setFixedDeadline] = useState(cue?.fixedDeadline ?? false);
  const [notes, setNotes] = useState(cue?.notes ?? '');

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', marginTop: 12 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>{cue ? `แก้ไขคิว ${cue.title}` : 'เพิ่มคิว'}</h4>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
        ชื่อคิว
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="เช่น Soundcheck, Doors open" />
      </label>

      <div className="ticket-edit-grid" style={{ marginTop: 12 }}>
        <label>
          เวลา (HH:MM)
          <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="18:30" />
        </label>
        <label>
          ความยาว (นาที)
          <input inputMode="numeric" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value.replace(/[^0-9]/g, ''))} />
        </label>
        <label>
          ฝ่าย
          <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">— ไม่ระบุ —</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <label>
          ผู้รับผิดชอบ
          <select value={ownerStaffId} onChange={(e) => setOwnerStaffId(e.target.value)}>
            <option value="">— ไม่ระบุ —</option>
            {crew.map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
          </select>
        </label>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
        <input type="checkbox" checked={fixedDeadline} onChange={(e) => setFixedDeadline(e.target.checked)} />
        เวลาตายตัว เลื่อนไม่ได้
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, marginTop: 12 }}>
        หมายเหตุ
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !title.trim() || !time.trim()}
          onClick={() => onSave({
            title: title.trim(),
            time: time.trim(),
            durationMinutes: Number(durationMinutes || 0),
            departmentId: departmentId || null,
            ownerStaffId: ownerStaffId || null,
            fixedDeadline,
            notes: notes.trim(),
          })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}

function DockEditor({ dock, busy, onCancel, onSave }: {
  dock: OpsDockDto | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: OpsDockInput) => void;
}) {
  const [dockNumber, setDockNumber] = useState(dock?.dockNumber ?? '');
  const [vendorName, setVendorName] = useState(dock?.vendorName ?? '');
  const [equipmentType, setEquipmentType] = useState(dock?.equipmentType ?? '');
  const [scheduledTime, setScheduledTime] = useState(dock?.scheduledTime ?? '');
  const [truckPlate, setTruckPlate] = useState(dock?.truckPlate ?? '');
  const [driverPhone, setDriverPhone] = useState(dock?.driverPhone ?? '');
  const [arrivedTime, setArrivedTime] = useState(dock?.arrivedTime ?? '');

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px', marginTop: 12 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>{dock ? `แก้ไขช่อง ${dock.dockNumber}` : 'เพิ่มรอบลงของ'}</h4>

      <div className="ticket-edit-grid">
        <label>
          ช่อง
          <input value={dockNumber} onChange={(e) => setDockNumber(e.target.value)} placeholder="เช่น D1" />
        </label>
        <label>
          เวลานัด (HH:MM)
          <input value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} placeholder="09:00" />
        </label>
        <label>
          ผู้ขาย
          <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
        </label>
        <label>
          อุปกรณ์
          <input value={equipmentType} onChange={(e) => setEquipmentType(e.target.value)} placeholder="เช่น Line array, Backline" />
        </label>
        <label>
          ทะเบียนรถ
          <input value={truckPlate} onChange={(e) => setTruckPlate(e.target.value)} />
        </label>
        <label>
          เบอร์คนขับ
          <input value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} />
        </label>
        <label>
          เวลาที่มาถึงจริง
          <input value={arrivedTime} onChange={(e) => setArrivedTime(e.target.value)} placeholder="ว่างไว้ถ้ายังไม่มา" />
        </label>
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 8, lineHeight: 1.7 }}>
        เบอร์คนขับเป็นข้อมูลส่วนบุคคล — ตารางรวมจะไม่แสดงเลขเต็ม และการกดดูรายคันถูกบันทึกไว้
      </div>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !dockNumber.trim() || !vendorName.trim() || !equipmentType.trim() || !scheduledTime.trim() || !truckPlate.trim() || !driverPhone.trim()}
          onClick={() => onSave({
            dockNumber: dockNumber.trim(),
            vendorName: vendorName.trim(),
            equipmentType: equipmentType.trim(),
            scheduledTime: scheduledTime.trim(),
            truckPlate: truckPlate.trim(),
            driverPhone: driverPhone.trim(),
            arrivedTime: arrivedTime.trim() === '' ? null : arrivedTime.trim(),
          })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}
